import Consts from '@/api/Consts';
import type { ActualGnssCode, GnssBadge } from '@/api/Consts';


type ActualGnssItem = Extract<typeof Consts.GNSS_DATA[number], { readonly isActual: true }>;

export type BrdcmGnssInts = Record<ActualGnssCode, number>;
export type BrdcmGnssBools = Record<ActualGnssCode, boolean>;

export type BrdcmStationPayload = {
	base_station: number;
	gnss: BrdcmGnssBools;
};

export type BrdcmPayload = {
	publish_interval_sec: number;
	cycle_delay_sec: number;
	buff_len: BrdcmGnssInts;
	max_toe_minutes: BrdcmGnssInts;
	stations: BrdcmStationPayload[];
};

export type BrdcmFormState = {
	publish_interval_sec: number | null;
	cycle_delay_sec: number | null;
	buff_len: Partial<Record<ActualGnssCode, number>>;
	stations: Record<number, BrdcmGnssBools>;
};

export type BrdcmSettingsProp = {
	brdcm?: BrdcmPayload;
};

export type BrdcmStationRow = {
	id: number;
	stationLabel: string;
	gnss: BrdcmGnssBools;
	gnssAllChecked: boolean;
	gnssIndeterminate: boolean;
	rtcmGnssBadges: GnssBadge[];
};

export type StationGnssCheckState = {
	checked: boolean;
	indeterminate: boolean;
};

type BrdcmStationItem = {
	id: number;
	mntpnt_int?: string;
	label?: string;
};


export const ACTUAL_GNSS_DATA = Consts.GNSS_DATA.filter(
	(item): item is ActualGnssItem => item.isActual,
);


export const createGnssRecord = <T>(makeValue: (code: ActualGnssCode) => T): Record<ActualGnssCode, T> => {
	const result = {} as Record<ActualGnssCode, T>;
	for (const item of ACTUAL_GNSS_DATA) {
		result[item.code] = makeValue(item.code);
	}
	return result;
};


export const createEmptyGnssBools = (): BrdcmGnssBools => createGnssRecord(() => false);


export const getStationGnssCheckState = (gnss: BrdcmGnssBools): StationGnssCheckState => {
	let onCount = 0;
	for (const item of ACTUAL_GNSS_DATA) {
		if (gnss[item.code]) {
			onCount += 1;
		}
	}
	const total = ACTUAL_GNSS_DATA.length;
	return {
		checked: onCount === total,
		indeterminate: onCount > 0 && onCount < total,
	};
};


export const setStationGnssAll = (gnss: BrdcmGnssBools, checked: boolean): void => {
	for (const item of ACTUAL_GNSS_DATA) {
		gnss[item.code] = checked;
	}
};


export const toggleStationGnssAll = (gnss: BrdcmGnssBools): void => {
	setStationGnssAll(gnss, !getStationGnssCheckState(gnss).checked);
};


export const getStationsGnssCheckState = (
	stationsGnss: Record<number, BrdcmGnssBools>,
	stationIds: ReadonlyArray<number>,
): StationGnssCheckState => {
	let onCount = 0;
	let total = 0;
	for (const stationId of stationIds) {
		const gnss = stationsGnss[stationId] ?? createEmptyGnssBools();
		for (const item of ACTUAL_GNSS_DATA) {
			total += 1;
			if (gnss[item.code]) {
				onCount += 1;
			}
		}
	}
	if (total === 0) {
		return {
			checked: false,
			indeterminate: false,
		};
	}
	return {
		checked: onCount === total,
		indeterminate: onCount > 0 && onCount < total,
	};
};


export const toggleStationsGnssAll = (
	stationsGnss: Record<number, BrdcmGnssBools>,
	stationIds: ReadonlyArray<number>,
): void => {
	const nextChecked = !getStationsGnssCheckState(stationsGnss, stationIds).checked;
	for (const stationId of stationIds) {
		const gnss = stationsGnss[stationId];
		if (gnss == null) {
			continue;
		}
		setStationGnssAll(gnss, nextChecked);
	}
};


export const createEmptyBrdcmFormState = (): BrdcmFormState => ({
	publish_interval_sec: null,
	cycle_delay_sec: null,
	buff_len: {},
	stations: {},
});


export const applyBrdcmSettings = (
	formState: BrdcmFormState,
	brdcm: BrdcmPayload | undefined,
	stations: ReadonlyArray<BrdcmStationItem>,
): void => {
	formState.publish_interval_sec = brdcm?.publish_interval_sec ?? null;
	formState.cycle_delay_sec = brdcm?.cycle_delay_sec ?? null;
	formState.buff_len = { ...(brdcm?.buff_len ?? {}) };
	const byId = new Map<number, BrdcmGnssBools>();
	for (const row of brdcm?.stations ?? []) {
		byId.set(row.base_station, {
			...createEmptyGnssBools(),
			...row.gnss,
		});
	}
	const stationsNew: Record<number, BrdcmGnssBools> = {};
	for (const station of stations) {
		stationsNew[station.id] = {
			...createEmptyGnssBools(),
			...(byId.get(station.id) ?? {}),
		};
	}
	formState.stations = stationsNew;
};


export const ensureBrdcmStationSlots = (
	formState: BrdcmFormState,
	stations: ReadonlyArray<BrdcmStationItem>,
): void => {
	for (const station of stations) {
		if (formState.stations[station.id] == null) {
			formState.stations[station.id] = createEmptyGnssBools();
		}
	}
};


export const serializeBrdcmFormState = (formState: BrdcmFormState) => {
	const stations: BrdcmStationPayload[] = [];
	for (const [stationId, gnss] of Object.entries(formState.stations)) {
		if (ACTUAL_GNSS_DATA.some(item => gnss[item.code])) {
			stations.push({
				base_station: Number(stationId),
				gnss: createGnssRecord(code => gnss[code]),
			});
		}
	}
	return {
		brdcm: {
			publish_interval_sec: formState.publish_interval_sec,
			cycle_delay_sec: Number(formState.cycle_delay_sec),
			buff_len: { ...formState.buff_len },
			stations,
		},
	};
};


const getStationSearchText = (station: BrdcmStationItem): { label: string; mntpnt: string } => ({
	label: station.label ?? '',
	mntpnt: station.mntpnt_int ?? '',
});


export const getRtcmGnssBadges = (rtcmMessages: readonly number[]): GnssBadge[] => (
	ACTUAL_GNSS_DATA.map(item => ({
		code: item.code,
		forceGrey: !rtcmMessages.includes(Consts.BRDCM_RTCM_EPHEMERIS_BY_GNSS[item.code]),
	}))
);


export const createBrdcmStationRows = (
	stations: ReadonlyArray<BrdcmStationItem>,
	stationsGnss: Record<number, BrdcmGnssBools>,
	query: string,
	rtcmByStationId: Readonly<Record<number, readonly number[]>> = {},
): BrdcmStationRow[] => {
	const normalizedQuery = query.trim().toLowerCase();
	return stations
		.filter(station => {
			if (!normalizedQuery) {
				return true;
			}
			const { label, mntpnt } = getStationSearchText(station);
			return label.toLowerCase().includes(normalizedQuery) || mntpnt.toLowerCase().includes(normalizedQuery);
		})
		.map(station => {
			const { label, mntpnt } = getStationSearchText(station);
			const gnss = stationsGnss[station.id] ?? createEmptyGnssBools();
			const checkState = getStationGnssCheckState(gnss);
			const rtcmMessages = rtcmByStationId[station.id] ?? [];
			return {
				id: station.id,
				stationLabel: mntpnt + ' | ' + label,
				gnss,
				gnssAllChecked: checkState.checked,
				gnssIndeterminate: checkState.indeterminate,
				rtcmGnssBadges: getRtcmGnssBadges(rtcmMessages),
			};
		});
};
